// Valeurs par défaut indicatives (normales de Paris 1991-2020, mm/mois)
export const DEFAULT_RAINFALL = [51, 44, 48, 53, 65, 54, 63, 43, 54, 60, 59, 59];

// Extrait les `count` premiers champs d'une ligne ';'-séparée, sans découper toute la ligne.
const getFields = (line, count) => {
  const out = [];
  let start = 0;
  for (let i = 0; i < count; i++) {
    const idx = line.indexOf(';', start);
    if (idx === -1) { out.push(line.slice(start)); start = line.length; break; }
    out.push(line.slice(start, idx));
    start = idx + 1;
  }
  return out;
};

/**
 * Parse un fichier "données climatologiques quotidiennes" Météo-France (CSV ou CSV.gz)
 * en streaming, et regroupe les précipitations quotidiennes (RR) par station/année/mois.
 * @param {File} file
 * @param {(text: string) => void} [onProgress]
 * @returns {Promise<{stations: Object, lineCount: number}>}
 */
export const parseMeteoFranceFile = async (file, onProgress) => {
  const isGz = /\.gz$/i.test(file.name);
  if (isGz && typeof DecompressionStream === 'undefined') {
    throw new Error(
      "Ce navigateur ne supporte pas la décompression gzip en direct. Décompresse le fichier (.gz → .csv, par ex. via l'explorateur de fichiers ou 'gunzip') puis dépose le .csv ici."
    );
  }

  const stations = {};
  let header = null, idx = {}, neededCount = 0;
  let lineCount = 0, chunkCount = 0;

  const processLine = (line) => {
    if (!line) return;
    lineCount++;
    if (header === null) {
      header = line.split(';').map(s => s.trim());
      ['NUM_POSTE', 'NOM_USUEL', 'LAT', 'LON', 'ALTI', 'AAAAMMJJ', 'RR'].forEach(name => { idx[name] = header.indexOf(name); });
      neededCount = Math.max(idx.NUM_POSTE, idx.NOM_USUEL, idx.LAT, idx.LON, idx.ALTI, idx.AAAAMMJJ, idx.RR) + 1;
      return;
    }
    const f = getFields(line, neededCount);
    const numPoste = f[idx.NUM_POSTE];
    const dateStr = f[idx.AAAAMMJJ];
    if (!numPoste || !dateStr || dateStr.length < 6) return;
    const year = parseInt(dateStr.slice(0, 4), 10);
    const month = parseInt(dateStr.slice(4, 6), 10) - 1;
    if (isNaN(year) || month < 0 || month > 11) return;
    if (!stations[numPoste]) {
      stations[numPoste] = { name: (f[idx.NOM_USUEL] || numPoste).trim(), lat: +f[idx.LAT], lon: +f[idx.LON], alt: +f[idx.ALTI], monthly: {}, counts: {} };
    }
    const st = stations[numPoste];
    if (!st.monthly[year]) { st.monthly[year] = Array(12).fill(0); st.counts[year] = Array(12).fill(0); }
    const rrStr = f[idx.RR];
    if (rrStr) {
      const rr = parseFloat(rrStr.replace(',', '.'));
      if (!isNaN(rr)) {
        st.monthly[year][month] += rr;
        st.counts[year][month] += 1;
      }
    }
  };

  let stream = file.stream();
  if (isGz) stream = stream.pipeThrough(new DecompressionStream('gzip'));
  const reader = stream.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) {
      if (buffer) processLine(buffer);
      break;
    }
    buffer += value;
    const parts = buffer.split('\n');
    buffer = parts.pop();
    for (const line of parts) processLine(line);
    chunkCount++;
    if (chunkCount % 20 === 0) onProgress?.(`Lecture en cours… ${lineCount.toLocaleString('fr-FR')} lignes traitées`);
  }

  if (idx.NUM_POSTE === -1 || idx.AAAAMMJJ === -1 || idx.RR === -1) {
    throw new Error("Colonnes attendues introuvables (NUM_POSTE / AAAAMMJJ / RR). Vérifie qu'il s'agit bien d'un fichier « RR-T-Vent » de Météo-France.");
  }
  if (Object.keys(stations).length === 0) {
    throw new Error('Aucune donnée exploitable trouvée dans ce fichier.');
  }

  return { stations, lineCount };
};

/**
 * Moyenne mensuelle sur [yearStart, yearEnd] : ne retient que les mois avec >=25 jours renseignés ;
 * si aucune année ne satisfait ce seuil pour un mois donné, on retombe sur les années
 * partiellement renseignées.
 */
export const computeStationMonthlyAverage = (station, yearStart, yearEnd) => {
  const strictSum = Array(12).fill(0), strictCnt = Array(12).fill(0);
  const anySum = Array(12).fill(0), anyCnt = Array(12).fill(0);
  for (let y = yearStart; y <= yearEnd; y++) {
    const monthly = station.monthly[y], counts = station.counts[y];
    if (!monthly) continue;
    for (let m = 0; m < 12; m++) {
      if (counts[m] > 0) {
        anySum[m] += monthly[m]; anyCnt[m]++;
        if (counts[m] >= 25) { strictSum[m] += monthly[m]; strictCnt[m]++; }
      }
    }
  }
  const values = Array(12).fill(0);
  let usedFallback = false;
  for (let m = 0; m < 12; m++) {
    if (strictCnt[m] > 0) values[m] = strictSum[m] / strictCnt[m];
    else if (anyCnt[m] > 0) { values[m] = anySum[m] / anyCnt[m]; usedFallback = true; }
    else usedFallback = true;
  }
  return { values, usedFallback };
};
